// ============================================================================
// quote-run-view.ts: the one Quote Run view model (SPEC-quote-run §2)
// ----------------------------------------------------------------------------
// One pure function, quoteRunView(input), says what the screen says: the
// headline, the job list in sections, every count, every row's words and its
// one action. Nothing on the screen counts on its own, so two surfaces cannot
// disagree about one job. quoteRunRowWords(row, ctx) is the row half, which
// the sheet's WAITING ON column, the workspace rail and the detail panel all
// read (run-blocker.ts delegates to it).
//
// Pure: no Dexie, no window, no clock of its own. `now` is passed in.
//
// Slice 2 covers S1 to S6 and S11 to S15 (§2.3, copy §4.1). `not_run` rows,
// Carry on and S7 to S10 arrive in slice 4. Until then a processing row whose
// page closed shows as "Stopped part way" in NOT RUN with an honest interim
// reason, and the headline falls through to the next state that is true.
//
// This module is the ONLY place that turns engine and job data into what a
// Quote Run surface shows about a job: its display state (QuoteRunDisplay),
// the word for it, its colours, the counts, the sentences and the one next
// step. The sheet and its filter chips, the job panel, the workspace rail, the
// Queue drawer and its summary, Browse's pill, filters and pulse, and the run
// bar all render from it and keep no mapping of their own, so no two of them
// can disagree about the same job.
// ============================================================================
import {
    builderDecisionOpenAction,
    canRetryFailedQueueJob,
    classifyFailedQueueRecovery,
    closedPageClaimHold,
    deriveProjectWorkflowState,
    failedJobNextStep,
    failureFamilyFor,
    IMPORT_WORKFLOW_TONE,
    isRepeatedFailure,
    isRunWideFailureCode,
    operatorFailureText,
    QUOTE_RUN_CLAIM_TTL_MS,
    quoteRunCoordinationOutcome,
} from '../file-explorer/import-workflow';
import type { ImportWorkflowState, QuoteRunCoordinationOutcome } from '../file-explorer/import-workflow';
import { handoffFollowFor, type HandoffFollow } from '../../utils/quote-run-brain-follow';

// ─── Shapes ─────────────────────────────────────────────────────────────────

export type SheetRow = {
    projectId: string;
    state: ImportWorkflowState;
    effectiveJob?: any;
    priorFailure?: any;
    position?: number | null;
    identity?: any;
    jobs?: any[];
    promotionReady?: boolean;
    promotionBlocked?: boolean;
    deferredMedia?: any[];
};

export type RowTone = 'active' | 'attn' | 'ok' | 'neutral';

export type RowActionKind =
    | 'add-to-run'
    | 'pick-files'
    | 'files-all-in'
    | 'decide-files'
    | 'leave-out'
    | 'watch'
    | 'check-draft'
    | 'open-stage'
    | 'review-latest'
    | 'retry'
    | 'restore-permission'
    | 'review-source'
    | 'repick-files'
    | 'start-over';

export type RowAction = {
    kind: RowActionKind;
    label: string;
    stage?: number;
    /** Printed with `reason` beside it, never a bare grey button. */
    disabled?: boolean;
};

export type SectionKey = 'running' | 'checking' | 'needs-you' | 'drafts' | 'waiting' | 'not-run' | 'parked';

/**
 * The one display state of a job, which every Quote Run surface renders.
 * The engine's `processing` splits three ways here: a page is working on it
 * (processing), its page may have closed and its lease has not ended
 * (checking), or its page closed (stopped). Only `processing` is running.
 */
export type QuoteRunDisplay =
    | 'ready'
    | 'staged'
    | 'waiting_files'
    | 'queued'
    | 'processing'
    | 'checking'
    | 'stopped'
    | 'needs_review'
    | 'failed';

/** The colour of a row's sentence (the sheet's WAITING ON column, the panel's status line). */
export type QuoteRunInk = 'slate' | 'amber' | 'accent' | 'violet' | 'red';

export type QuoteRunDisplayEntry = {
    /** The word for the state: filter chips, summaries, pills with no row words. */
    label: string;
    /** Hover text for that word. */
    description: string;
    /** The colours, until slice 5 moves them to the Quote Run tone layer. */
    palette: ImportWorkflowState;
    /** A page is working on it: only then a live pulse, a running clock or a running count. */
    running: boolean;
    /** How a count of it reads in a line of counts. */
    count: (n: number) => string;
};

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/** Every display state, its word, its colours and how a count of it reads. */
export const QUOTE_RUN_DISPLAY: Readonly<Record<QuoteRunDisplay, QuoteRunDisplayEntry>> = Object.freeze({
    ready: {
        label: 'Ready',
        description: 'Not on the Quote Run list.',
        palette: 'ready',
        running: false,
        count: (n: number) => `${n} ready`,
    },
    staged: {
        label: 'Parked',
        description: 'Files picked. Not in the run.',
        palette: 'staged',
        running: false,
        count: (n: number) => `${n} parked`,
    },
    waiting_files: {
        label: 'Waiting on files',
        description: 'Parked while its files are still arriving.',
        palette: 'waiting_files',
        running: false,
        count: (n: number) => `${n} waiting on files`,
    },
    queued: {
        label: 'Waiting to start',
        description: 'On the run list. It has not started.',
        palette: 'queued',
        running: false,
        count: (n: number) => `${n} waiting to start`,
    },
    processing: {
        label: 'Running now',
        description: 'A page is working on it now, in this tab or another.',
        palette: 'processing',
        running: true,
        count: (n: number) => `${n} running now`,
    },
    checking: {
        label: 'Checking',
        description: 'QuoteMaker is checking whether the page that ran it is still open. It is not counted as running.',
        palette: 'queued',
        running: false,
        count: (n: number) => `${n} checking`,
    },
    stopped: {
        label: 'Stopped part way',
        // Its page closed, or this tab's run ended without finishing it.
        description: 'It stopped part way through. It is not running.',
        palette: 'waiting_files',
        running: false,
        count: (n: number) => `${n} stopped part way`,
    },
    // The run's part is done: a draft to check, or a job handed to the Quote
    // Brain (completionOutcome quote_brain_handoff). Each row's own chip says
    // which, so the count does not call a hand-off a draft.
    needs_review: {
        label: 'Finished',
        description: 'The run is done with it: a draft to check, or a quote being written. Nothing has been sent.',
        palette: 'needs_review',
        running: false,
        count: (n: number) => `${n} finished`,
    },
    failed: {
        label: 'Needs you',
        description: 'It stopped and needs you.',
        palette: 'failed',
        running: false,
        count: (n: number) => `${n} ${plural(n, 'needs', 'need')} you`,
    },
});

/** The order counts, filters and summaries list the states in. */
export const QUOTE_RUN_DISPLAY_ORDER: ReadonlyArray<QuoteRunDisplay> = Object.freeze([
    'ready', 'staged', 'waiting_files', 'queued', 'processing', 'checking', 'stopped', 'needs_review', 'failed',
]);

/** A display state's colours (IMPORT_WORKFLOW_TONE, until slice 5's tone layer). */
export function quoteRunColours(palette: ImportWorkflowState): { color: string; bg: string; border: string } {
    const tone = IMPORT_WORKFLOW_TONE[palette] || IMPORT_WORKFLOW_TONE.ready;
    return { color: tone.color, bg: tone.bg, border: tone.border };
}

export type RowWords = {
    projectId: string;
    jobId: number | string | null;
    state: ImportWorkflowState;
    /** The one display state every surface renders for this job. */
    display: QuoteRunDisplay;
    /** The chip's colours: the display's, or a coordination or no-progress stop's own. */
    palette: ImportWorkflowState;
    /** The sentence's colour. */
    ink: QuoteRunInk;
    section: SectionKey | null;
    chip: string;
    tone: RowTone;
    /** The folder name picked for the run, for the life of the row (G13). */
    name: string;
    /** The quote title once known, else ''. */
    subline: string;
    sentence: string;
    /**
     * The whole plain sentence behind `sentence`, for the panel. The S12
     * headline prints it too, less a coordination stop's words about the
     * panel's Other choices (the headline has only the row's action).
     */
    detail: string;
    age: string;
    action: RowAction | null;
    /** Why there is no action, or why the action is disabled. */
    reason: string | null;
    flags: {
        live: boolean;
        /** A running job this tab's own run is driving (its tab and stop lines). */
        drivenHere: boolean;
        interrupted: boolean;
        /**
         * Interrupted, and claimed by this page's own runner: this tab's run
         * ended without finishing it (a lease renewal failed and the run
         * threw). Its page is this one, still open, so its words never say
         * the page closed or that another tab runs it.
         */
        stoppedHere: boolean;
        /**
         * A job last written before this page loaded whose page may still be
         * open (a duplicated tab's original) or may have closed (a reload).
         * Neither running nor stopped until its lease ends (claimCheckEndsAt).
         */
        checking: boolean;
        /** A running job the queue says has made no progress for a while. */
        stalled: boolean;
        /** A stop that is coordination between sessions, and which kind. */
        coordination: QuoteRunCoordinationOutcome['kind'] | null;
        leaseWaitSeconds: number;
        /**
         * A job whose page closed while it held its server claim: the seconds
         * until that claim lets go. A new attempt before then is refused as
         * busy (the server takes over only an expired claim), so the wait to
         * reopen Quote Run is the later of this and the lease.
         */
        claimWaitSeconds: number;
        stage4Review: boolean;
        repeatedFailure: boolean;
        next: boolean;
        /** Waiting, but not one of the jobs this tab's run froze: it runs in the next run. */
        nextRun: boolean;
        /**
         * Waiting in this run, but Stop after this job is armed: the run
         * stops before it starts (batch-queue.js cancels it as not run).
         */
        afterStop: boolean;
        filesRead: boolean;
        /**
         * Its files were read, and the local proof that its saved copy is
         * confirmed could not be read here (collapseQueueJobs
         * promotionProofReadable false): no draft is promised for it.
         */
        proofUnknown: boolean;
    };
};

export type ViewEngine = {
    active?: boolean;
    paused?: boolean;
    currentJobId?: number | string | null;
    phase?: string | null;
    stopAfterCurrent?: boolean;
    /** The claimOwnerId this page's runner writes on every job it claims (getQueueStatus). */
    runnerOwnerId?: string | null;
    /** The jobs this run froze when it started, the only ones it claims (getQueueStatus). */
    cohortJobIds?: ReadonlyArray<number | string> | null;
    /** The run reads no more files: it is past its reading phase (getQueueStatus). */
    readingDone?: boolean;
    /**
     * The batch of the startQueue call now running (getQueueStatus). A wait
     * for the AI service that call wrote carries it (providerRetryBatchId),
     * and only that call waits the wait out (runWaitsOutProviderFor).
     */
    batchId?: string | null;
};

/**
 * The window events batch-queue.js raises when this tab's engine changes
 * with no batch_jobs write: Stop after this job and Keep going, a pause, a
 * resume, a phase's drain, and the end of a run. A surface that re-reads only
 * on a table write (useBatchJobsLiveSignal) re-derives its rows on these too,
 * or it keeps the old words until the running job's next lease heartbeat.
 *
 * A Quote Run is a prepare-all run: its drains arrive while
 * getQueueStatus() still says active, so only 'qm-batch-queue-idle', raised
 * once the whole run is over, re-reads an idle engine (review of 6da18a4e).
 */
export const QUOTE_RUN_ENGINE_EVENTS: ReadonlyArray<string> = Object.freeze([
    'qm-batch-queue-stop-after-current',
    'qm-batch-queue-paused',
    'qm-batch-queue-resumed',
    'qm-batch-queue-drained',
    'qm-batch-queue-idle',
]);

/**
 * The one line the run bar and the Queue drawer print for this tab's paused
 * run, or null when it is not paused (or Stop after this job is armed, whose
 * own line says the run stops). The engine pauses between jobs only
 * (batch-queue.js pauseQueue): a job in progress finishes first, and its row
 * on every surface shows it running meanwhile. Both lines end on Carry on,
 * the one name both surfaces give the control that lifts the pause.
 */
export function quoteRunPausedLine(
    engine: { active?: boolean; paused?: boolean; stopAfterCurrent?: boolean; currentJobId?: unknown } | null | undefined,
    jobName: string | null | undefined,
): string | null {
    if (!engine?.active || !engine.paused || engine.stopAfterCurrent) return null;
    if (engine.currentJobId != null) {
        return `The run pauses after ${jobName || 'the job in progress'}. Nothing new starts until you press Carry on.`;
    }
    return 'The run is paused between jobs. Nothing starts until you press Carry on.';
}

export type ViewRun = {
    lastRunEnd?: { kind: string; [key: string]: unknown } | null;
    /** A4: this tab's run marker. `ownerId` is the runner its claims carry. */
    ownMarker?: { startedAt: string; ownerId?: string | null } | null;
    /**
     * The runners of this tab's earlier pages (quote-run-controls readOwnRun).
     * A job one of them claimed has no page behind it, whatever its lease says.
     */
    earlierOwnerIds?: ReadonlyArray<string> | null;
    /**
     * When this page loaded (quote-run-controls readPageLoadedAt). A claim
     * written to after it belongs to a live page, even one whose runner id
     * this tab remembers (a duplicated tab inherits the original's).
     */
    pageLoadedAt?: number | null;
    /**
     * This page's own runner (quote-run-controls readOwnRun). A job it
     * claimed that no run of this page is driving has no page working on it,
     * and its page (this one) is open: this tab's run stopped part way.
     */
    currentOwnerId?: string | null;
    stopping?: boolean;
    pendingOpen?: unknown;
    busy?: boolean;
};

export type RowContext = {
    engine?: ViewEngine | null;
    run?: ViewRun | null;
    now?: number;
    /** Run-wide rank from assignRunPositions; only rank 1 says Next. */
    rank?: number | null;
    /** Jobs still to run, for the open-during-a-run reason. */
    runRemaining?: number;
    /** The running job's PipelineMonitor step name, when one is known. */
    subStep?: string | null;
};

export type ViewControl = {
    kind: string;
    label: string;
    ids?: Array<number | string>;
    projectIds?: string[];
    projectId?: string;
    jobId?: number | string | null;
    stage?: number;
    disabled?: boolean;
};

export type ViewSection = {
    key: SectionKey;
    title: string;
    head: string;
    count: number;
    rows: RowWords[];
};

export type QuoteRunViewState =
    | 'S1' | 'S2' | 'S3' | 'S4' | 'S5' | 'S6'
    | 'S11' | 'S12' | 'S13' | 'S14' | 'S15'
    | 'checking'
    | 'interim-stopped';

export type QuoteRunView = {
    state: QuoteRunViewState;
    headline: string;
    detail: string;
    notes: string[];
    primary: ViewControl | null;
    primaryDisabledReason: string | null;
    quiet: ViewControl | null;
    more: ViewControl[];
    sections: ViewSection[];
    counts: Record<SectionKey, number> & { total: number };
    /** A2 folder line, slice 4. */
    folderLine: null;
};

export type QuoteRunViewInput = {
    rows: SheetRow[];
    engine?: ViewEngine | null;
    run?: ViewRun | null;
    folder?: { name: string; projectIds: string[] } | null;
    adding?: { done: number; total: number } | null;
    updateState?: string | null;
    now: number;
    subStep?: string | null;
};

// ─── Small pure helpers (shared with run-blocker.ts) ────────────────────────

/** Short honest age. No timestamp means no text, never a guess. */
export function shortAge(iso?: string | null, now: number = Date.now()): string {
    if (!iso) return '';
    const then = new Date(iso).getTime();
    if (!Number.isFinite(then) || then <= 0) return '';
    const mins = Math.floor((now - then) / 60000);
    if (mins < 1) return 'now';
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h`;
    return `${Math.floor(hours / 24)}d`;
}

export function rowAgeIso(row: SheetRow): string | null {
    const job = row?.effectiveJob || {};
    return job.completedAt || job.startedAt || job.enqueuedAt || job.stagedAt || null;
}

/**
 * The files the job will read: its picks, or for a job whose files are read
 * when its turn starts (deferred intake), the file records it will read. A
 * job with neither reads every project file, and counts none here.
 */
export function rowFileCount(row: SheetRow): number {
    const job = row?.effectiveJob || {};
    if (Array.isArray(job.selectedFileIds)) return job.selectedFileIds.length;
    const deferred = job.deferredIntake?.fileRecords;
    return Array.isArray(deferred) ? deferred.length : 0;
}

/** Whether the job's files are on record (rowFileCount), so a count of 0 is true. */
function rowFilesKnown(row: SheetRow): boolean {
    const job = row?.effectiveJob || {};
    return Array.isArray(job.selectedFileIds) || Array.isArray(job.deferredIntake?.fileRecords);
}

/**
 * A job with no picks on record reads every file of its project: the runners
 * read a null selection as all project files (batch-queue.js
 * _normaliseSelectedFileIds, and an empty pick list is stored as null). So its
 * file count is not 0 and it is never asked for a pick. A parked job is the
 * exception: it is still being set up, and its words ask for files
 * (quoteRunRowWords 'staged').
 */
export function rowReadsAllFiles(row: SheetRow): boolean {
    if (row?.state === 'staged' || row?.state === 'waiting_files') return false;
    return !rowFilesKnown(row);
}

/**
 * The files a set of jobs will read, in one phrase: the picks counted, and
 * the jobs that read every project file named, never counted as 0.
 */
export function quoteRunFilesTotal(rows: ReadonlyArray<SheetRow>): string {
    const list = Array.isArray(rows) ? rows : [];
    const all = list.filter((row) => rowReadsAllFiles(row)).length;
    const picked = list.filter((row) => !rowReadsAllFiles(row)).reduce((total, row) => total + rowFileCount(row), 0);
    const pickedJobs = list.length - all;
    if (all === 0) return `${filesWord(picked)} in total`;
    if (pickedJobs === 0) return all === 1 ? 'All its project files' : 'All project files in each job';
    return `${filesWord(picked)}, plus all project files in ${jobsWord(all)}`;
}

/**
 * A job that is waiting out a paused AI provider, and when it resumes.
 * batch-queue.js parks it as 'pending' with providerWaitUntil and
 * providerRetryCount. Live, 2026-09-20: an operator twice concluded a
 * 16-project run had died while it was waiting out rate limits.
 */
export function providerWaitFor(
    row: SheetRow,
    now: number = Date.now(),
): { resumesInMs: number; attempt: number; connection: boolean } | null {
    const job: any = row?.effectiveJob || {};
    if (job.status !== 'pending') return null;
    const resumesAt = Date.parse(String(job.providerWaitUntil || ''));
    if (!Number.isFinite(resumesAt) || resumesAt <= now) return null;
    return {
        resumesInMs: resumesAt - now,
        attempt: Number(job.providerRetryCount) || 0,
        // The wait records the code it is waiting out (batch-queue.js writes
        // lastErrorCode with providerWaitUntil). NETWORK_FAILED is the
        // connection or QuoteMaker's own service, not the AI service.
        connection: String(job.lastErrorCode || '').trim().toUpperCase() === 'NETWORK_FAILED',
    };
}

/**
 * A wait for the AI service that this tab's run is waiting out, so it tries
 * the job again by itself when the wait ends; else null (review of 97f9e0dd).
 * Only the startQueue call that wrote the wait honours it (batch-queue.js
 * providerWaitPending, a loop in that call's own memory). The engine claims a
 * pending job by its place in line and ignores providerWaitUntil, and nothing
 * clears it. So after a reload, a crash or a closed tab nothing waits and
 * nothing retries it: it runs when a run reaches it. A later run claims it in
 * its turn, and a paused run waits for Carry on after the wait. So the wait
 * is this tab's run's only while that run goes, is not paused, and is the one
 * that wrote it (its batch: providerRetryBatchId, getQueueStatus batchId).
 */
export function runWaitsOutProviderFor(
    row: SheetRow,
    engine: ViewEngine | null | undefined,
    now: number = Date.now(),
): ReturnType<typeof providerWaitFor> {
    const wait = providerWaitFor(row, now);
    if (!wait || !engine?.active || engine.paused) return null;
    const writtenBy = String(row?.effectiveJob?.providerRetryBatchId || '');
    if (!writtenBy || writtenBy !== String(engine.batchId || '')) return null;
    return wait;
}

/** How long the queue has seen no progress on a running job, if it has said so. */
export function noProgressFor(row: SheetRow, now: number = Date.now()): number | null {
    const since = Date.parse(String(row?.effectiveJob?.stalledSince || ''));
    if (!Number.isFinite(since) || since <= 0 || since > now) return null;
    return now - since;
}

// A run does every pre-processing job first, then every prepare-quote job
// (startAllQueuedProjects). Queue positions are assigned PER PHASE, so one
// queued job of each phase both carried position 1. Rank across the whole run
// instead, in the order the run will actually reach them.
const PHASE_RUN_ORDER: Record<string, number> = { 'pre-processing': 0, 'prepare-quote': 1 };

function isFilesReadJob(job: any): boolean {
    return (job?.phase || 'prepare-quote') === 'pre-processing' && job?.status === 'completed';
}

/**
 * The job a waiting row puts in the run: its newest pending job, else its own
 * job (a "Files read" row's completed intake). Null when it has none.
 */
function rowRunJob(row: SheetRow): any {
    const jobs: any[] = Array.isArray(row?.jobs) ? row.jobs : [];
    const pending = jobs.filter((job) => job?.status === 'pending');
    return (pending.length > 0 ? newestOf(pending) : row?.effectiveJob) || null;
}

function rowRunJobId(row: SheetRow): number | string | null {
    const id = rowRunJob(row)?.id;
    return id === null || id === undefined ? null : id;
}

/**
 * True while this tab's run is going and the waiting row's job is not one the
 * run will still take. The run claims only the jobs it froze when it started
 * (getQueueStatus cohortJobIds; batch-queue.js startQueue,
 * startAllQueuedProjects), so a job put back after the start (Start over, Add
 * to the run, Try again, Restore folder access) runs in the next run; the last
 * three keep the job's id, and the engine takes it out of the frozen jobs
 * (_leaveActiveRun). It reads every job's files first, then drafts, and never
 * goes back to reading: a reading job waiting once the reading is over waits
 * for the next run, and so does a job whose files were read but not moved on
 * to drafting once the drafting has begun. With no run here, nothing is
 * claimed about it.
 */
export function waitsForNextRun(row: SheetRow, engine?: ViewEngine | null): boolean {
    if (!engine?.active || row?.state !== 'queued') return false;
    const runJob = rowRunJob(row);
    const drafting = engine.phase === 'prepare-quote';
    if (runJob?.status === 'pending' && (runJob?.phase || 'prepare-quote') === 'pre-processing'
        && (drafting || engine.readingDone === true)) {
        return true;
    }
    if (drafting && isFilesReadJob(runJob)) return true;
    const cohort = Array.isArray(engine.cohortJobIds) ? engine.cohortJobIds : [];
    if (cohort.length === 0) return false;
    const id = rowRunJobId(row);
    if (id === null) return false;
    return !cohort.some((candidate) => String(candidate) === String(id));
}

/**
 * The run-wide rank of each waiting row. Pass this tab's engine so a job that
 * waits for the next run (waitsForNextRun) is not ranked in this one.
 */
export function assignRunPositions(rows: SheetRow[], engine?: ViewEngine | null): Map<string, number> {
    const queued = (Array.isArray(rows) ? rows : []).filter((row) => (
        row?.state === 'queued' && !isFilesReadJob(row.effectiveJob) && !waitsForNextRun(row, engine)
    ));
    const ordered = queued
        .map((row, index) => {
            const job: any = row.effectiveJob || {};
            const position = Number(row?.position);
            return {
                row,
                index,
                phase: PHASE_RUN_ORDER[String(job.phase || 'prepare-quote')] ?? 1,
                position: Number.isFinite(position) && position > 0 ? position : Number.MAX_SAFE_INTEGER,
            };
        })
        .sort((left, right) => (
            left.phase - right.phase
            || left.position - right.position
            || left.index - right.index
        ));
    const ranks = new Map<string, number>();
    ordered.forEach((entry, rank) => {
        if (entry.row?.projectId) ranks.set(String(entry.row.projectId), rank + 1);
    });
    return ranks;
}

const jobTime = (job: any) => new Date(job?.completedAt || job?.startedAt || job?.enqueuedAt || 0).getTime() || 0;
const newestOf = (jobs: any[]) => [...jobs].sort((a, b) => (
    (jobTime(b) - jobTime(a)) || (Number(b?.id || 0) - Number(a?.id || 0))
))[0];

/**
 * The one job each waiting row puts in the run: its pending job, or for a
 * "Files read" row (intake complete, waiting to be moved on to drafting) its
 * completed intake. jobCount is the number of rows that contribute, which is
 * the number Start prints (G3). quote-run-controls re-exports this.
 */
export function runSelectionForRows(rows: ReadonlyArray<any>): { jobCount: number; ids: Array<number | string> } {
    const ids: Array<number | string> = [];
    for (const row of Array.isArray(rows) ? rows : []) {
        if (!row || row.state !== 'queued') continue;
        const jobs: any[] = Array.isArray(row.jobs) ? row.jobs : [];
        const pending = jobs.filter((job) => job?.status === 'pending');
        let chosen = pending.length > 0 ? newestOf(pending) : null;
        if (!chosen && row.promotionReady) {
            chosen = newestOf(jobs.filter((job) => (
                (job?.phase || 'prepare-quote') === 'pre-processing' && job?.status === 'completed'
            ))) || null;
        }
        const id = chosen?.id;
        if (id === null || id === undefined || ids.includes(id)) continue;
        ids.push(id);
    }
    return { jobCount: ids.length, ids };
}

// ─── Words ──────────────────────────────────────────────────────────────────

const jobsWord = (n: number) => `${n} ${plural(n, 'job', 'jobs')}`;
const filesWord = (n: number) => `${n} ${plural(n, 'file', 'files')}`;

function minutesText(ms: number): string {
    const mins = Math.floor(Math.max(0, ms) / 60000);
    return mins < 1 ? 'under 1 min' : `${mins} min`;
}

function stallText(ms: number): string {
    const seconds = Math.floor(Math.max(0, ms) / 1000);
    return seconds < 60 ? `${seconds} s` : `${Math.floor(seconds / 60)} min`;
}

function waitText(ms: number): string {
    const seconds = Math.round(Math.max(0, ms) / 1000);
    return seconds < 60 ? `${seconds} s` : `${Math.round(seconds / 60)} min`;
}

function cleanText(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
}

function isPlaceholderName(value: string): boolean {
    return !value
        || /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
        || ['queued project', 'untitled project', 'project details loading…'].includes(value.toLowerCase());
}

function lastSegment(path: string): string {
    return path.replace(/\\/g, '/').split('/').map((part) => part.trim()).filter(Boolean).pop() || '';
}

/**
 * The folder name picked for the run, fixed for the life of the row (G13,
 * E24). The folder path is the stable anchor; a quote title arriving mid-run
 * becomes the second line and never renames the row.
 */
export function jobFolderName(row: SheetRow): string {
    const meta = row?.effectiveJob?.displayMeta || row?.effectiveJob?.projectMeta || {};
    const fromPath = lastSegment(cleanText(meta.relativePath) || cleanText(meta.path));
    if (fromPath) return fromPath;
    const candidates = [meta.name, meta.projectName, meta.address, row?.identity?.name]
        .map(cleanText)
        .filter((value) => !isPlaceholderName(value));
    return candidates[0] || String(row?.projectId || '').trim();
}

/** The quote title for line 2, once one is known and it says something new. */
export function jobQuoteTitle(row: SheetRow): string {
    const name = jobFolderName(row);
    const candidates = [
        row?.effectiveJob?.quoteTitle,
        (row as any)?.project?.quoteTitle,
        row?.identity?.hasHumanName === false ? '' : row?.identity?.name,
    ].map(cleanText).filter((value) => !isPlaceholderName(value));
    const title = candidates[0] || '';
    return title && title.toLocaleLowerCase() !== name.toLocaleLowerCase() ? title : '';
}

function numericStage(value: unknown): number | null {
    const stage = Number(value);
    return Number.isFinite(stage) && stage > 0 ? stage : null;
}

function isReadingJob(job: any): boolean {
    return (job?.phase || 'prepare-quote') === 'pre-processing'
        || job?.currentStage === 'materialize-files'
        || job?.currentStage === 'preprocess';
}

function firstSentence(text: string): string {
    const trimmed = String(text || '').trim();
    const match = trimmed.match(/^(.+?[.!?])(?=\s|$)/);
    return (match ? match[1]! : trimmed).trim();
}

const stripStop = (text: string) => text.replace(/[.]$/, '');

// ─── Row flags (display only, never written) ────────────────────────────────

function leaseState(job: any, now: number): { known: boolean; live: boolean; waitSeconds: number } {
    const expires = Date.parse(String(job?.leaseExpiresAt || ''));
    if (!Number.isFinite(expires)) return { known: false, live: false, waitSeconds: 0 };
    const live = expires > now;
    return { known: true, live, waitSeconds: live ? Math.ceil((expires - now) / 1000) : 0 };
}

function claimedByThisRunner(job: any, engine: ViewEngine): boolean {
    const owner = engine.runnerOwnerId ? String(engine.runnerOwnerId) : '';
    return !!owner && !!job?.claimOwnerId && String(job.claimOwnerId) === owner;
}

/**
 * Has this tab's run opened the job it is on? The engine records its claim
 * on the project (quoteRunClaimedAt) just before pipelineRunner.loadProject,
 * whose batch load names the job the run is driving; from then on opening it
 * shows it (use-project-persistence showQuoteRunJobInsteadOfOpening). Before
 * that an open is refused with "Stop the Quote Run?". A claim time older than
 * this attempt's start belongs to an earlier attempt of the same job.
 */
function runHasOpened(job: any): boolean {
    const claimed = Date.parse(String(job?.quoteRunClaimedAt || ''));
    if (!Number.isFinite(claimed)) return false;
    const started = Date.parse(String(job?.startedAt || ''));
    return !Number.isFinite(started) || claimed >= started;
}

/**
 * Seconds until the server claim of a job whose page closed lets go. Only a
 * drafting job that took a claim in this attempt and never released it holds
 * one. Every heartbeat renews the lease and then the claim, for
 * QUOTE_RUN_CLAIM_TTL_MS (batch-queue.js), and a renewal does not write
 * quoteRunClaimExpiresAt, so the claim ends at the later of that and the last
 * heartbeat plus the claim's life. Nothing releases it when a page closes:
 * until then a new attempt is refused as busy.
 */
function claimHoldSeconds(job: any, now: number): number {
    if ((job?.phase || 'prepare-quote') !== 'prepare-quote') return 0;
    if (!job?.quoteRunClaimId || job?.quoteRunClaimReleasedAt || !runHasOpened(job)) return 0;
    const ends = [
        Date.parse(String(job?.quoteRunClaimExpiresAt || '')),
        Date.parse(String(job?.leaseHeartbeatAt || '')) + QUOTE_RUN_CLAIM_TTL_MS,
    ].filter((value) => Number.isFinite(value));
    if (ends.length === 0) return 0;
    const end = Math.max(...ends);
    return end > now ? Math.ceil((end - now) / 1000) : 0;
}

/** How long before Quote Run, opened again, can put a stopped job back: its lease and its claim. */
function reopenWaitSeconds(words: RowWords): number {
    return Math.max(words.flags.leaseWaitSeconds, words.flags.claimWaitSeconds);
}

/**
 * Was this claim last written before this page loaded? Only then can it be an
 * earlier page's: that page stopped writing when this one loaded, while a live
 * claimer keeps renewing its lease (batch-queue.js _renewJobLease). The last
 * write is the newest of the lease heartbeat, the project claim and the job's
 * start. With no load time or no timestamp on record, it is read as an
 * earlier page's, as before.
 */
function claimLastWrittenBeforeThisPage(job: any, pageLoadedAt: unknown): boolean {
    const loaded = Number(pageLoadedAt);
    if (!Number.isFinite(loaded) || loaded <= 0) return true;
    const last = claimLastWriteAt(job);
    if (last === null) return true;
    return last < loaded;
}

/** The newest write of a claim: its lease heartbeat, the project claim or its start. */
function claimLastWriteAt(job: any): number | null {
    const writes = [job?.leaseHeartbeatAt, job?.quoteRunClaimedAt, job?.startedAt]
        .map((value) => Date.parse(String(value || '')))
        .filter((value) => Number.isFinite(value));
    return writes.length > 0 ? Math.max(...writes) : null;
}

/**
 * When a claim last written before this page loaded can be judged: when its
 * lease ends. A duplicated tab inherits the original's runner ids and marker,
 * and the original last renewed its claim before the duplicate loaded, exactly
 * as an earlier page of this tab did before a reload. The two look the same
 * until the original's next renewal lands (the claim is then written after
 * this page loaded, and it reads as running in another tab) or the lease ends
 * with none (a closed page). The lease is the engine's own answer to that
 * question: batch-queue.js renews it every 20 s and lets it run 90 s
 * (JOB_LEASE_DURATION_MS), because a hidden tab's timers fire about once a
 * minute and a tick is skipped while the last one is in flight, and it resets
 * a processing job to waiting only once the lease has ended (hydrate,
 * recoverExpiredQueueLeases). A fixed window shorter than the lease called a
 * hidden original's live job closed between its renewals. Null when the
 * claim can be judged now: no load time, no write on record, a write after
 * this page loaded, or a lease that has ended.
 */
function claimCheckEndsAt(job: any, pageLoadedAt: unknown, now: number): number | null {
    const loaded = Number(pageLoadedAt);
    if (!Number.isFinite(loaded) || loaded <= 0) return null;
    const lease = leaseState(job, now);
    if (!lease.live) return null;
    const last = claimLastWriteAt(job);
    if (last === null || last >= loaded) return null;
    return Date.parse(String(job?.leaseExpiresAt));
}

function processingFlags(row: SheetRow, ctx: RowContext) {
    const job = row?.effectiveJob || {};
    const engine = ctx.engine || {};
    const now = Number.isFinite(ctx.now) ? Number(ctx.now) : Date.now();
    const lease = leaseState(job, now);
    const isCurrent = engine.currentJobId != null && job.id != null
        && String(engine.currentJobId) === String(job.id);
    // A job this tab is driving is live: the one the engine names, or one its
    // own runner claimed and has not named yet (the checks after a claim).
    // With no job named (a wait for a busy AI service, between jobs) the
    // engine vouches for nothing else: a job another tab or a closed page
    // claimed is judged by its lease, like any other. The run claims only the
    // jobs it froze when it started (cohortJobIds), so a job this runner
    // claimed in an earlier run of this page, and left processing when that
    // run threw, is not this run's once the run has frozen its jobs.
    const cohort = Array.isArray(engine.cohortJobIds) ? engine.cohortJobIds : [];
    const frozenByThisRun = cohort.length === 0 || cohort.some((id) => String(id) === String(job.id));
    if (engine.active && (isCurrent || (claimedByThisRunner(job, engine) && frozenByThisRun))) {
        return { live: true, drivenHere: true, isCurrent, interrupted: false, checking: false, stoppedHere: false, leaseWaitSeconds: 0 };
    }
    // Closed, unless the page that claimed it may still be open: then it is
    // neither running nor stopped until its lease ends (claimCheckEndsAt).
    const closedOrChecking = () => (claimCheckEndsAt(job, ctx.run?.pageLoadedAt, now) !== null
        ? { live: false, drivenHere: false, isCurrent: false, interrupted: false, checking: true, stoppedHere: false, leaseWaitSeconds: lease.waitSeconds }
        : { live: false, drivenHere: false, isCurrent: false, interrupted: true, checking: false, stoppedHere: false, leaseWaitSeconds: lease.waitSeconds });
    // This page's own runner claimed it, and no run of this page is driving
    // it: the run that claimed it ended without finishing it (review of
    // 97f9e0dd: one failed lease renewal ends the run with _LeaseLostError and
    // leaves the job processing, its lease live). No page is working on it,
    // and its page is this one, still open: not another tab's run, not a
    // closed page's. Its lease runs out with no renewal.
    const thisPage = ctx.run?.currentOwnerId
        ? String(ctx.run.currentOwnerId)
        : (engine.active && engine.runnerOwnerId ? String(engine.runnerOwnerId) : '');
    if (thisPage && job.claimOwnerId && String(job.claimOwnerId) === thisPage) {
        return { live: false, drivenHere: false, isCurrent: false, interrupted: true, checking: false, stoppedHere: true, leaseWaitSeconds: lease.waitSeconds };
    }
    // A job an earlier page of this tab claimed (a reload or a crash) has no
    // page behind it, whatever its lease says, even while this tab runs again.
    // sessionStorage belongs to the tab, so those runners are this tab's own,
    // except in a duplicated tab, which inherits them from a tab that may
    // still be running. So the claim counts as an earlier page's only when it
    // was last written before this page loaded, and its lease has ended with
    // no renewal.
    const claimOwner = job.claimOwnerId ? String(job.claimOwnerId) : '';
    const earlier = Array.isArray(ctx.run?.earlierOwnerIds) ? ctx.run!.earlierOwnerIds! : [];
    const pageLoadedAt = ctx.run?.pageLoadedAt;
    const writtenBeforeThisPage = claimLastWrittenBeforeThisPage(job, pageLoadedAt);
    if (claimOwner && earlier.some((id) => String(id) === claimOwner) && writtenBeforeThisPage) {
        return closedOrChecking();
    }
    // This tab's own run died (reload or crash): the marker says the run was
    // ours, the engine is idle, so the row is not running whatever its lease.
    // The marker outlives the reload, so it only speaks for the jobs its own
    // runner claimed (batch-queue.js claimOwnerId): a job another tab claimed
    // later is that tab's live run. A marker or a job with no owner on record
    // (written before owners were kept) is read as this tab's. A marker from
    // before this page loaded (a reload, or a copy in a duplicated tab) speaks
    // only for a claim last written before this page loaded, once its lease
    // has ended with no renewal. A marker this page wrote speaks at once:
    // this page's own runner is idle.
    const marker = ctx.run?.ownMarker;
    const markerOwner = marker?.ownerId ? String(marker.ownerId) : '';
    const loadedAt = Number(pageLoadedAt);
    const markerAt = Date.parse(String(marker?.startedAt || ''));
    const markerFromThisPage = Number.isFinite(loadedAt) && loadedAt > 0 && Number.isFinite(markerAt) && markerAt >= loadedAt;
    if (!engine.active && marker && (!markerOwner || !claimOwner || markerOwner === claimOwner)
        && (markerFromThisPage || writtenBeforeThisPage)) {
        if (markerFromThisPage) {
            return { live: false, drivenHere: false, isCurrent: false, interrupted: true, checking: false, stoppedHere: false, leaseWaitSeconds: lease.waitSeconds };
        }
        return closedOrChecking();
    }
    // Otherwise the lease is the cross-tab truth (§2.2). Every claim writes
    // one (batch-queue.js _claimNextPendingJob), so a processing row without
    // a live lease has no page behind it. A live one is another tab's run.
    if (lease.live) {
        return { live: true, drivenHere: false, isCurrent: false, interrupted: false, checking: false, stoppedHere: false, leaseWaitSeconds: lease.waitSeconds };
    }
    return { live: false, drivenHere: false, isCurrent: false, interrupted: true, checking: false, stoppedHere: false, leaseWaitSeconds: 0 };
}

function isStage4Review(row: SheetRow): boolean {
    if (row?.state !== 'needs_review') return false;
    return row?.effectiveJob?.completionOutcome === 'stage4_review_required';
}

const OPEN_THE_JOB: RowAction = { kind: 'open-stage', label: 'Open the job' };

/**
 * A handed-off job's words, from the last read of its server run (null before
 * the first read, or where nothing reads it). They name the work, never the
 * architecture: "Quote Brain" is an internal name and customers never see it
 * (Peter, 2026-11-01). The job's banner has the controls (Continue, Try again,
 * Compare), so every state but a written quote opens the job; a written one
 * opens the quote.
 */
function handoffWords(follow: HandoffFollow | null): { chip: string; tone: RowTone; ink: QuoteRunInk; sentence: string; action: RowAction } {
    const withReason = (lead: string, reason: string | null) => (reason ? `${lead}: ${stripStop(reason)}` : lead);
    switch (follow?.state) {
        case 'starting':
            return follow.retrying
                ? { chip: 'Writing quote', tone: 'attn', ink: 'amber', sentence: `${withReason('The quote has not started yet', follow.reason)}. It tries again each minute`, action: OPEN_THE_JOB }
                : { chip: 'Writing quote', tone: 'active', ink: 'accent', sentence: 'Starting the quote', action: OPEN_THE_JOB };
        case 'writing':
            return { chip: 'Writing quote', tone: 'active', ink: 'accent', sentence: 'Writing the quote', action: OPEN_THE_JOB };
        case 'written':
            return { chip: 'Quote written', tone: 'ok', ink: 'violet', sentence: 'Nothing sent yet', action: { kind: 'open-stage', label: 'Open quote', stage: 5 } };
        case 'held':
            return { chip: 'Check quote', tone: 'attn', ink: 'amber', sentence: 'A new quote waits for you to compare', action: OPEN_THE_JOB };
        case 'paused':
            return { chip: 'Paused', tone: 'attn', ink: 'amber', sentence: `${withReason('Writing the quote paused', follow.reason)}. Continue it on the job`, action: OPEN_THE_JOB };
        case 'stopped':
            return {
                chip: follow.failed ? 'Not written' : 'Stopped',
                tone: 'attn',
                ink: follow.failed ? 'red' : 'amber',
                sentence: `${withReason(follow.failed ? 'The quote could not be finished' : 'Writing the quote was stopped', follow.reason)}. The job is as it was`,
                action: OPEN_THE_JOB,
            };
        case 'not-started':
            return { chip: 'Not started', tone: 'attn', ink: 'amber', sentence: withReason('The quote was not started', follow.reason), action: OPEN_THE_JOB };
        default:
            return { chip: 'Writing quote', tone: 'ok', ink: 'violet', sentence: 'Summary saved. QuoteMaker writes the quote next', action: OPEN_THE_JOB };
    }
}

// ─── Failed rows: words and the one action ──────────────────────────────────

function stoppedAtWords(job: any): string {
    const stage = numericStage(job?.lastErrorStage ?? job?.currentStage);
    return stage ? `Stopped at Stage ${stage}.` : 'Stopped.';
}

function failedRowAction(row: SheetRow, repeated: boolean, now: number): { action: RowAction | null; reason: string | null } {
    const job = row?.effectiveJob || {};
    const coordination = quoteRunCoordinationOutcome(job, now);
    if (coordination) {
        // Try again would be refused (the engine marked the stop final), so
        // after the look the row offers the step the job does have.
        if (coordination.action === 'next-step' && coordination.step) {
            const step = coordination.step;
            if (step.kind === 'elsewhere') return { action: null, reason: step.why };
            return { action: { kind: step.kind, label: step.label }, reason: null };
        }
        return {
            action: { kind: coordination.action === 'review' ? 'review-latest' : 'retry', label: coordination.actionLabel },
            reason: null,
        };
    }
    const builder = builderDecisionOpenAction(job);
    if (builder) return { action: { kind: 'open-stage', label: builder.label, stage: builder.stage }, reason: null };

    // One answer for the row, the panel's Other choices (run-blocker
    // retryPlan) and the words (operatorFailureText): failedJobNextStep.
    // E22: a saved intake that stopped the same way after a retry already
    // classifies as the folder read (classifyFailedQueueRecovery), so its
    // step is Read the files again here, in Browse and in the drawer alike.
    const step = failedJobNextStep(job);
    if (step.kind === 'elsewhere') return { action: null, reason: step.why };
    if (repeated && step.kind === 'retry') {
        const stage = numericStage(job.lastErrorStage ?? job.currentStage);
        return {
            action: stage
                ? { kind: 'open-stage', label: `Open Stage ${stage}`, stage }
                : { kind: 'open-stage', label: 'Open the job' },
            reason: null,
        };
    }
    return { action: { kind: step.kind, label: step.label }, reason: null };
}

// A sentence that sends the operator back to a retry: the family's own next
// step, which a repeated stop has already tried.
const RETRY_STEP = /\btry again\b|\bretry\b|^if it stops the same way/i;

/**
 * The words without the family's own next step. `nextLabel` is the step the
 * repeat line names after it (Open Stage N, Read the files again): a family
 * sentence that names it too goes, so it is said once, after the repeat.
 */
function withoutRetryStep(text: string, nextLabel?: string): string {
    return text
        .split(/(?<=[.!?])\s+/)
        .filter((part) => part.trim()
            && !RETRY_STEP.test(part.trim())
            && !(nextLabel && part.includes(nextLabel)))
        .join(' ');
}

/**
 * A coordination stop's headline and panel words: its title, what happened,
 * and what its own button does. Only the words: the button is the outcome's.
 * A stale write's button opens the project; its Try again is under the
 * panel's Other choices only while the job can carry on (RunCard). The
 * headline has one button and no Other choices, so its words (`where`
 * 'headline') name only that button.
 */
function coordinationDetail(
    job: any,
    coordination: QuoteRunCoordinationOutcome,
    where: 'panel' | 'headline' = 'panel',
): string {
    const lead = `${coordination.title}.`;
    // Its description already names the step the row offers.
    if (coordination.action === 'next-step') return `${lead} ${coordination.description}`;
    if (coordination.kind === 'busy') {
        // A closed page's own claim says when it lets go, never "that session".
        const when = coordination.hold
            ? (coordination.hold.held ? ' after that' : '')
            : ' once that session has finished';
        return `${lead} ${coordination.description} Press ${coordination.actionLabel}${when}.`;
    }
    if (coordination.kind === 'stale') {
        const retry = where === 'panel' && classifyFailedQueueRecovery(job) === 'continue-quote'
            ? ' After you check it, Try again under Other choices reloads it and carries on.'
            : '';
        return `${lead} Another tab or device saved this project first, so nothing here was overwritten. `
            + `${coordination.actionLabel} opens that version.${retry}`;
    }
    if (coordination.action === 'review') {
        // After the look the job offers Try again, or the step it has when
        // Try again would be refused (coordination.step).
        const step = coordination.step;
        const then = !step
            ? 'then this job offers Try again.'
            : step.kind === 'elsewhere' ? `then: ${step.why}` : `then this job offers ${step.label}.`;
        // This job stopped; the run did not pause (quoteRunCoordinationOutcome).
        return `${lead} QuoteMaker stopped this job before its next stage. Completed work is retained. `
            + `${coordination.actionLabel} opens the latest project, ${then}`;
    }
    return `${lead} ${coordination.description} Press ${coordination.actionLabel} to carry on.`;
}

function failedWords(row: SheetRow, repeated: boolean, action: RowAction | null, now: number) {
    const job = row?.effectiveJob || {};
    const coordination = quoteRunCoordinationOutcome(job, now);
    if (coordination) {
        // The calm coordination line on the row, with no closing stop like
        // every other row sentence; the headline and panel say what happened
        // and what the one button does.
        return {
            chip: coordination.title,
            tone: (coordination.tone === 'slate' ? 'neutral' : 'attn') as RowTone,
            sentence: stripStop(coordination.description),
            detail: coordinationDetail(job, coordination),
        };
    }
    if (row?.promotionBlocked || (job.status === 'completed' && isFilesReadJob(job))) {
        const detail = "QuoteMaker read this job's files but could not confirm the saved copy, so it did not start the draft. Pick its files again.";
        return { chip: 'Needs you', tone: 'attn' as RowTone, sentence: stripStop(firstSentence(detail)), detail };
    }
    const plain = operatorFailureText(job) || stoppedAtWords(job);
    let detail = plain;
    let sentence = stripStop(firstSentence(plain));
    if (repeated) {
        const next = action?.kind === 'open-stage'
            ? ` ${action.label} to see where it stopped.`
            : action?.kind === 'review-source'
                ? ' Read the files again reads the folder afresh.'
                : '';
        // G14: the family's own "Try again." is the step that just failed,
        // so it goes before the sentence that says so.
        const kept = withoutRetryStep(plain, next ? action?.label : undefined);
        detail = `${kept ? `${kept} ` : ''}Tried again and it stopped the same way.${next}`;
        sentence = `${sentence}. Tried again and it stopped the same way`;
    }
    return { chip: 'Needs you', tone: 'attn' as RowTone, sentence, detail };
}

// ─── One row ────────────────────────────────────────────────────────────────

function stoppedPartWayAdvice(count: number, waitSeconds: number, runGoingHere: boolean): string {
    // Quote Run puts the job back by itself once its lease has run out
    // (FileExplorer, batch-queue.js recoverExpiredQueueLeases): it then waits
    // to start and runs when a run is started, so the words promise no more
    // than that. A lease still live is not reset, so the words carry its wait.
    // While this tab's run goes nothing is reset until the run has ended.
    const it = count === 1 ? 'It goes' : 'They go';
    if (runGoingHere) return `After this run ends, ${lowerFirst(it)} back to Waiting to start.`;
    return waitSeconds > 0
        ? `${it} back to Waiting to start in ${waitSeconds} s.`
        : `${it} back to Waiting to start.`;
}

function interimStoppedReason(waitSeconds: number, runGoingHere: boolean): string {
    return stoppedPartWayAdvice(1, waitSeconds, runGoingHere);
}

/**
 * The toast after Start over. It sets the job waiting to start again; it
 * does not start it. A run freezes the jobs it will run when it starts
 * (batch-queue.js startAllQueuedProjects), so during a run the job waits for
 * the next one, and with no run going it waits for Start the run.
 */
export function startOverDoneText(runGoing: boolean): string {
    return runGoing
        ? 'Waiting to start again, from its file picks. It runs in the next run.'
        : 'Waiting to start again, from its file picks. It runs when you start the run.';
}

function interruptedSentence(stageValue: unknown, finishedSaved: boolean): string {
    const stage = numericStage(stageValue);
    const tail = finishedSaved ? ' Finished stages are saved.' : '';
    if (stage) return `Stopped when the page closed, at Stage ${stage}.${tail}`;
    if (stageValue === 'materialize-files' || stageValue === 'preprocess') {
        return 'Stopped when the page closed, while its files were read.';
    }
    return 'Stopped when the page closed.';
}

/**
 * A job this tab's own run left part way (processingFlags stoppedHere): the
 * page it ran on is this one and is open, so the words say the run stopped,
 * where, and never that a page closed. No stage claim when none is on record.
 */
function stoppedHereSentence(stageValue: unknown, finishedSaved = false): string {
    const stage = numericStage(stageValue);
    const tail = finishedSaved ? ' Finished stages are saved.' : '';
    if (stage) return `The run in this tab stopped at Stage ${stage}.${tail}`;
    if (stageValue === 'materialize-files' || stageValue === 'preprocess') {
        return 'The run in this tab stopped while its files were read.';
    }
    return 'The run in this tab stopped while this job was running.';
}

/**
 * A waiting job whose lease was reset (batch-queue.js hydrate,
 * recoverExpiredQueueLeases): where it stopped, and why. The reset records
 * the runner that held it (interruptedOwnerId). When that is this page's own
 * runner, this tab's run stopped and the page never closed (review of
 * d89088c1: Quote Run opened again, as the stopped row advised, then said the
 * page closed). Any other runner, or none on record, was a page that closed.
 */
function resetJobSentence(job: any, ctx: RowContext): string {
    const owner = job?.interruptedOwnerId ? String(job.interruptedOwnerId) : '';
    const thisPage = ctx.run?.currentOwnerId ? String(ctx.run.currentOwnerId) : '';
    if (owner && thisPage && owner === thisPage) return stoppedHereSentence(job.interruptedAtStage, true);
    return interruptedSentence(job.interruptedAtStage, true);
}

/** Where each display state sits in the list (§2.4). A ready job is not on it. */
const DISPLAY_SECTION: Readonly<Record<QuoteRunDisplay, SectionKey | null>> = Object.freeze({
    ready: null,
    staged: 'parked',
    waiting_files: 'parked',
    queued: 'waiting',
    processing: 'running',
    checking: 'checking',
    stopped: 'not-run',
    needs_review: 'drafts',
    failed: 'needs-you',
});

export function quoteRunRowWords(row: SheetRow, ctx: RowContext = {}): RowWords {
    const now = Number.isFinite(ctx.now) ? Number(ctx.now) : Date.now();
    const job = row?.effectiveJob || {};
    const files = rowFileCount(row);
    // No selection on record means the runner reads every project file, so
    // the row must not claim "0 files".
    const filesKnown = rowFilesKnown(row);
    const base = {
        projectId: String(row?.projectId || ''),
        jobId: job.id ?? null,
        state: row?.state,
        name: jobFolderName(row),
        subline: jobQuoteTitle(row),
        age: shortAge(rowAgeIso(row), now),
    };
    const flags = {
        live: false,
        drivenHere: false,
        interrupted: false,
        stoppedHere: false,
        checking: false,
        stalled: false,
        coordination: null as QuoteRunCoordinationOutcome['kind'] | null,
        leaseWaitSeconds: 0,
        claimWaitSeconds: 0,
        stage4Review: false,
        repeatedFailure: false,
        next: false,
        nextRun: false,
        afterStop: false,
        filesRead: false,
        proofUnknown: false,
    };
    // The display state, the chip's colours and the sentence's colour. The
    // palette is the display's unless a row's own look differs (a stop that
    // is coordination between sessions, a running job with no progress).
    type Look = { display: QuoteRunDisplay; ink: QuoteRunInk; palette?: ImportWorkflowState };
    const words = (
        look: Look,
        chip: string,
        tone: RowTone,
        sentence: string,
        action: RowAction | null,
        reason: string | null,
        detail?: string,
    ): RowWords => ({
        ...base,
        display: look.display,
        palette: look.palette || QUOTE_RUN_DISPLAY[look.display].palette,
        ink: look.ink,
        section: DISPLAY_SECTION[look.display],
        chip,
        tone,
        sentence,
        detail: detail ?? sentence,
        action,
        reason,
        flags,
    });

    switch (row?.state) {
        case 'processing': {
            const state = processingFlags(row, ctx);
            flags.live = state.live;
            flags.drivenHere = state.drivenHere;
            flags.interrupted = state.interrupted;
            flags.stoppedHere = state.stoppedHere;
            flags.checking = state.checking;
            flags.leaseWaitSeconds = state.leaseWaitSeconds;
            if (state.interrupted) {
                // Its page closed holding its server claim: Quote Run, opened
                // again, puts it back once the lease ends, but a run started
                // before the claim lets go is refused as busy. A job this
                // tab's own run left part way reads the same, in words that
                // say where it stopped, not that its page closed.
                flags.claimWaitSeconds = claimHoldSeconds(job, now);
                return words(
                    { display: 'stopped', ink: 'amber' },
                    'Stopped part way',
                    'attn',
                    state.stoppedHere ? stoppedHereSentence(job.currentStage) : interruptedSentence(job.currentStage, false),
                    null,
                    interimStoppedReason(Math.max(state.leaseWaitSeconds, flags.claimWaitSeconds), !!ctx.engine?.active),
                );
            }
            if (state.checking) {
                // Its page may still be open (a duplicated tab's original), or
                // may have closed (a reload): neither is claimed until its
                // lease ends. Not running: no clock, no pulse, not counted.
                // quoteRunRedrawIn keeps the seconds moving.
                return words(
                    { display: 'checking', ink: 'slate' },
                    'Checking',
                    'neutral',
                    'Checking whether its page is still open',
                    null,
                    `Another tab may still be running it. QuoteMaker knows within ${state.leaseWaitSeconds} s.`,
                );
            }
            const started = Date.parse(String(job.startedAt || ''));
            const elapsed = Number.isFinite(started) ? minutesText(now - started) : '';
            // Watch shows the job this tab's run has opened, without loading
            // it. Any other job would be opened, and an open during a run is
            // refused with "Stop the Quote Run?": another tab's job (which
            // would also race that tab's run), and this run's own job while
            // its files are read or before the run has opened it.
            const reading = isReadingJob(job);
            let watch: RowAction | null = null;
            let watchReason: string | null = null;
            if (!state.drivenHere) watchReason = 'Running in another tab. Its controls are there.';
            else if (reading) watchReason = 'Can be watched once its draft starts.';
            else if (!state.isCurrent || !runHasOpened(job)) watchReason = 'Can be watched once the run has opened it.';
            else watch = { kind: 'watch', label: 'Watch' };
            const stalled = noProgressFor(row, now);
            flags.stalled = stalled != null;
            // A running job with no progress keeps its display state (it is
            // still running) and reads amber, as the queue reports it.
            const look: Look = stalled != null
                ? { display: 'processing', ink: 'amber', palette: 'waiting_files' }
                : { display: 'processing', ink: 'accent' };
            if (reading) {
                const sentence = stalled != null
                    ? `No progress for ${stallText(stalled)}`
                    : [filesKnown && files > 0 ? `Reading ${filesWord(files)}` : 'Reading files', elapsed].filter(Boolean).join(' · ');
                return words(look, 'Reading files', 'active', sentence, watch, watchReason);
            }
            const stage = numericStage(job.currentStage) || 1;
            const parts = [`Stage ${stage} of 5`];
            if (stalled != null) parts.push(`No progress for ${stallText(stalled)}`);
            else {
                if (ctx.subStep) parts.push(String(ctx.subStep));
                if (elapsed) parts.push(elapsed);
            }
            return words(look, 'Writing the draft', 'active', parts.join(' · '), watch, watchReason);
        }
        case 'queued': {
            // Put back after this tab's run started, so the run will not
            // claim it: it is not Next and not one of this run's jobs.
            flags.nextRun = waitsForNextRun(row, ctx.engine);
            // Stop after this job: the run ends once the job in progress
            // finishes, and the jobs it froze and had not started are
            // cancelled as not run (batch-queue.js requestStopAfterCurrentJob).
            // So none of them is Next. A job waiting for the next run is not
            // one of them and still waits for it.
            flags.afterStop = !flags.nextRun && !!(ctx.engine?.active && ctx.engine?.stopAfterCurrent);
            const rank = Number(ctx.rank ?? NaN);
            flags.next = rank === 1 && !flags.nextRun && !flags.afterStop;
            if (isFilesReadJob(job)) {
                flags.filesRead = true;
                // Only a confirmed saved copy moves on to a draft
                // (collapseQueueJobs promotionReady). Where that proof
                // cannot be read (the rail, when this browser's project
                // records cannot be read), no draft is promised.
                if (!row?.promotionReady) {
                    flags.proofUnknown = true;
                    return words({ display: 'queued', ink: 'slate' }, 'Files read', 'neutral',
                        'Its saved copy is not confirmed yet', null, 'Quote Run shows its next step.');
                }
                const look: Look = { display: 'queued', ink: 'accent' };
                if (flags.nextRun) return words(look, 'Files read', 'neutral', 'Runs in the next run', null, 'Not in this run');
                if (flags.afterStop) return words(look, 'Files read', 'neutral', 'The run stops before its draft', null, 'Not in this run');
                return words(look, 'Files read', 'neutral', 'Draft next', null, 'In this run');
            }
            const chip = flags.next ? 'Next' : 'Waiting to start';
            const tone: RowTone = flags.next ? 'active' : 'neutral';
            const leaveOut: RowAction = { kind: 'leave-out', label: 'Leave out' };
            const waiting: Look = { display: 'queued', ink: 'slate' };
            if (flags.nextRun || flags.afterStop) {
                const stopped = job.interruptedAt ? `${resetJobSentence(job, ctx)} ` : '';
                flags.interrupted = !!job.interruptedAt;
                const when = flags.nextRun ? 'Runs in the next run' : 'The run stops before it starts';
                return words(waiting, chip, tone, `${stopped}${when}`, leaveOut, null);
            }
            // A wait for the AI service is the newest fact about the job, and
            // a retry is promised only while this tab's run waits it out.
            const wait = runWaitsOutProviderFor(row, ctx.engine, now);
            if (wait) {
                const what = wait.connection ? 'Connection lost' : 'AI service busy';
                return words({ display: 'queued', ink: 'amber' }, chip, tone,
                    `${what}, trying again in ${waitText(wait.resumesInMs)}`, leaveOut, null);
            }
            if (job.interruptedAt) {
                flags.interrupted = true;
                return words(waiting, chip, tone, resetJobSentence(job, ctx), leaveOut, null);
            }
            const sentence = flags.next
                ? (filesKnown ? `Starts next · ${filesWord(files)}` : 'Starts next')
                : (filesKnown ? filesWord(files) : 'All its files');
            return words(waiting, chip, tone, sentence, leaveOut, null);
        }
        case 'failed': {
            const repeated = isRepeatedFailure(job);
            flags.repeatedFailure = repeated;
            const { action, reason } = failedRowAction(row, repeated, now);
            const text = failedWords(row, repeated, action, now);
            // A stop that is coordination between sessions reads calm (slate)
            // or amber, never the red of a failure.
            const coordination = quoteRunCoordinationOutcome(job, now);
            flags.coordination = coordination?.kind ?? null;
            const look: Look = coordination
                ? (coordination.tone === 'slate'
                    ? { display: 'failed', ink: 'slate', palette: 'staged' }
                    : { display: 'failed', ink: 'amber', palette: 'waiting_files' })
                : { display: 'failed', ink: 'red' };
            return words(look, text.chip, text.tone, text.sentence, action, reason, text.detail);
        }
        case 'needs_review': {
            const stage4 = isStage4Review(row);
            flags.stage4Review = stage4;
            const engineActive = !!ctx.engine?.active;
            const left = Math.max(0, Number(ctx.runRemaining) || 0);
            // Slice 2 keeps today's rule: a draft opens between runs. Slice 3
            // lets it open during a run by stopping after the current job.
            const lockReason = engineActive
                ? (left > 0 ? `Opens when the run finishes. ${jobsWord(left)} left.` : 'Opens when the run finishes.')
                : null;
            if (stage4) {
                const issues = Array.isArray(job.reviewDetails?.issues) ? job.reviewDetails.issues : [];
                const issue = cleanText(issues.find((entry: unknown) => typeof entry === 'string' && entry.trim()));
                const sentence = issue ? `Stage 4 needs a look: ${issue}` : 'Stage 4 needs a look';
                return words({ display: 'needs_review', ink: lockReason ? 'slate' : 'amber' }, 'Check scope', 'attn', sentence,
                    { kind: 'open-stage', label: 'Open Stage 4', stage: 4, ...(lockReason ? { disabled: true } : {}) },
                    lockReason);
            }
            // The run wrote the summary and handed the job to QuoteMaker
            // (pipeline-runner.js D2). batch-queue.js stores it as
            // completionOutcome 'quote_brain_handoff', as v6.0.135 did, and
            // the row follows QuoteMaker's run on the job until the quote is
            // written (quote-run-brain-follow.ts), so no one opens each job.
            if (job.completionOutcome === 'quote_brain_handoff') {
                const said = handoffWords(handoffFollowFor(row?.projectId));
                return words({ display: 'needs_review', ink: lockReason ? 'slate' : said.ink }, said.chip, said.tone, said.sentence,
                    { ...said.action, ...(lockReason ? { disabled: true } : {}) },
                    lockReason);
            }
            const setAside = Array.isArray(row?.deferredMedia) ? row.deferredMedia.length : 0;
            // A Quote Run that kept its draft without the AI quote says so (2026-10-23).
            const aiQuoteNotWritten = typeof job?.aiQuoteNotWritten === 'string' && job.aiQuoteNotWritten.trim() !== '';
            const sentence = [
                'Nothing sent yet',
                aiQuoteNotWritten ? 'AI quote not written' : null,
                setAside > 0 ? `${filesWord(setAside)} set aside` : null,
            ].filter(Boolean).join(' · ');
            return words({ display: 'needs_review', ink: lockReason ? 'slate' : 'violet' }, 'Draft ready', 'ok', sentence,
                { kind: 'check-draft', label: 'Check draft', ...(lockReason ? { disabled: true } : {}) },
                lockReason);
        }
        case 'staged':
            return files > 0
                ? words({ display: 'staged', ink: 'slate' }, 'Parked', 'neutral', `${filesWord(files)} picked, not in the run`, { kind: 'add-to-run', label: 'Add to the run' }, null)
                : words({ display: 'staged', ink: 'amber' }, 'Parked', 'neutral', 'No files picked yet', { kind: 'pick-files', label: 'Pick files' }, null);
        case 'waiting_files': {
            const look: Look = { display: 'waiting_files', ink: 'amber' };
            const arrivals: any[] = Array.isArray(job.arrivalRelPaths) ? job.arrivalRelPaths : [];
            if (arrivals.length > 0) {
                return words(look, 'Waiting on files', 'neutral', `${arrivals.length} new ${plural(arrivals.length, 'file', 'files')} to decide`,
                    { kind: 'decide-files', label: `Decide ${filesWord(arrivals.length)}` }, null);
            }
            const allIn: RowAction = { kind: 'files-all-in', label: 'Files are all in' };
            if (job.owedNote) return words(look, 'Waiting on files', 'neutral', `Owed: ${String(job.owedNote)}`, allIn, null);
            const packs: any[] = Array.isArray(job.waitingPacks) ? job.waitingPacks : [];
            const outstanding = packs.find((pack) => pack?.expectation === 'waiting' && !pack?.firstArrivalAt);
            if (outstanding) {
                return words(look, 'Waiting on files', 'neutral', `Waiting on ${String(outstanding.label || outstanding.relPrefix)}`, allIn, null);
            }
            return words(look, 'Waiting on files', 'neutral', 'Waiting on files', allIn, null);
        }
        default:
            // A cleared draft (and, until slice 4, a stopped job) derives
            // 'ready': it is not on the Quote Run list.
            return words({ display: 'ready', ink: 'slate' }, '', 'neutral', '', null, null);
    }
}

/**
 * The one word a surface prints for a job's state where it has no room for
 * the row's own chip: the row's chip when it has one, else its display
 * state's word.
 */
export function quoteRunChipText(words: Pick<RowWords, 'chip' | 'display'>): string {
    return words.chip || QUOTE_RUN_DISPLAY[words.display].label;
}

/**
 * Browse's words for a folder. A folder whose job is on the Quote Run list
 * reads as its row does (the same words the sheet, the drawer and the rail
 * print); a folder with no job reads its project's own state, the one
 * deriveProjectWorkflowState gives with no job on record (Ready, or a draft
 * or a stop recorded on the project).
 */
export function quoteRunFolderLook(
    project: any,
    words: RowWords | null,
): { display: QuoteRunDisplay; label: string; palette: ImportWorkflowState; running: boolean } {
    if (words) {
        return {
            display: words.display,
            label: quoteRunChipText(words),
            palette: words.palette,
            running: QUOTE_RUN_DISPLAY[words.display].running,
        };
    }
    const own = deriveProjectWorkflowState(project, []);
    const display: QuoteRunDisplay = own === 'needs_review' || own === 'failed' ? own : 'ready';
    const entry = QUOTE_RUN_DISPLAY[display];
    return { display, label: entry.label, palette: entry.palette, running: false };
}

/**
 * Every count a Quote Run surface prints, from each row's own words: one
 * count per display state (QUOTE_RUN_DISPLAY), plus the jobs this run will
 * still do. The sheet's chips, caption and pre-flight, the job panel's
 * jobs-left count, the Queue drawer's summary and launcher, Browse's filters
 * and the run bar all read this, never row.state, so no line calls a job
 * running that its row does not.
 */
export type QuoteRunCounts = Record<QuoteRunDisplay, number> & {
    /** Jobs on the Quote Run list: every display state but ready. */
    total: number;
    /** Running now (display processing): here or in another tab. */
    running: number;
    /** Waiting to start, and one of the jobs this run will do. */
    waiting: number;
    /** Waiting to start, but it runs in the next run. */
    nextRun: number;
    /** Stopped part way (display stopped). */
    stoppedPartWay: number;
    /** The jobs the run will still do (rowIsInRun), the sheet caption's count. */
    inRun: number;
    /** The longest wait before a stopped job's lease ends. */
    stoppedWaitSeconds: number;
    /** The longest wait before a checked job's lease ends. */
    checkingWaitSeconds: number;
};

export function quoteRunCounts(rows: ReadonlyArray<SheetRow>, ctx: RowContext = {}): QuoteRunCounts {
    return countWords(rowsWords(rows, ctx), ctx.engine);
}

function rowsWords(rows: ReadonlyArray<SheetRow>, ctx: RowContext): RowWords[] {
    return (Array.isArray(rows) ? rows : []).map((row) => quoteRunRowWords(row, ctx));
}

function countWords(all: ReadonlyArray<RowWords>, engine?: ViewEngine | null): QuoteRunCounts {
    const counts = Object.fromEntries(QUOTE_RUN_DISPLAY_ORDER.map((key) => [key, 0])) as QuoteRunCounts;
    counts.total = 0;
    counts.waiting = 0;
    counts.nextRun = 0;
    counts.inRun = 0;
    counts.stoppedWaitSeconds = 0;
    counts.checkingWaitSeconds = 0;
    for (const words of all) {
        counts[words.display] += 1;
        if (words.display !== 'ready') counts.total += 1;
        if (words.display === 'queued') {
            if (words.flags.nextRun) counts.nextRun += 1;
            else counts.waiting += 1;
        }
        if (words.display === 'stopped') counts.stoppedWaitSeconds = Math.max(counts.stoppedWaitSeconds, reopenWaitSeconds(words));
        if (words.display === 'checking') counts.checkingWaitSeconds = Math.max(counts.checkingWaitSeconds, words.flags.leaseWaitSeconds);
        if (rowIsInRun(words, engine)) counts.inRun += 1;
    }
    counts.running = counts.processing;
    counts.stoppedPartWay = counts.stopped;
    return counts;
}

/**
 * Is this row one of the jobs this tab's run will still do, or, with no run
 * here, one Start the run would send (runSelectionForRows)? A waiting job the
 * run froze, and the job this tab's run is driving. A job another tab is
 * running is that tab's run, with a run here or not: this tab neither drives
 * it nor can start it. Nor is a job put back after the start (flags.nextRun),
 * one the run stops before (flags.afterStop), one whose files were read but
 * whose saved copy is not confirmed here (flags.proofUnknown), or one being
 * checked or stopped part way. `engine` is kept for callers; the row's words
 * already carry what it says.
 */
export function rowIsInRun(words: RowWords, engine?: ViewEngine | null): boolean {
    void engine;
    if (words.display === 'queued') return !words.flags.nextRun && !words.flags.afterStop && !words.flags.proofUnknown;
    if (words.display === 'processing') return words.flags.drivenHere;
    return false;
}

/**
 * The jobs whose page closed part way, and the one sentence that says what to
 * do about all of them. The interim headline and the run bar print it beside
 * the rows' own reasons. The wait is the longest of their leases: opening
 * Quote Run again resets only an expired lease (batch-queue.js hydrate), so
 * after that wait every one of them goes back.
 */
export function quoteRunStoppedPartWay(
    rows: ReadonlyArray<SheetRow>,
    ctx: RowContext = {},
): { count: number; waitSeconds: number; detail: string } {
    const counts = quoteRunCounts(rows, ctx);
    const count = counts.stopped;
    const waitSeconds = counts.stoppedWaitSeconds;
    return {
        count,
        waitSeconds,
        detail: count > 0 ? stoppedPartWayAdvice(count, waitSeconds, !!ctx.engine?.active) : '',
    };
}

/**
 * How long until what a row says changes with the clock alone. The screens
 * re-render when batch_jobs is written, and nothing is written when another
 * page's lease runs out (the job then reads Stopped part way) or while a
 * countdown is on screen (the wait to reopen Quote Run, the check on a page
 * that may have closed, a wait for a busy AI service). Null when nothing
 * shown depends on the clock. Display only: the caller re-renders at that
 * time and writes nothing.
 */
export function quoteRunRedrawIn(rows: ReadonlyArray<SheetRow>, ctx: RowContext = {}): number | null {
    const now = Number.isFinite(ctx.now) ? Number(ctx.now) : Date.now();
    let soonest: number | null = null;
    const consider = (ms: number) => {
        if (Number.isFinite(ms) && ms >= 0 && (soonest === null || ms < soonest)) soonest = ms;
    };
    for (const row of Array.isArray(rows) ? rows : []) {
        if (row?.state === 'processing') {
            const lease = leaseState(row.effectiveJob, now);
            const words = quoteRunRowWords(row, { ...ctx, now });
            if (words.display === 'stopped') {
                // The reason counts down to the end of the lease, and of the
                // closed page's server claim, while no run goes here.
                if (!ctx.engine?.active && reopenWaitSeconds(words) > 0) consider(1000);
                continue;
            }
            if (!lease.live) continue;
            if (words.display === 'checking') {
                // The words count down to the end of the lease, when the job
                // reads Stopped part way (a renewal after this page loaded
                // redraws it as running, on its own write).
                consider(1000);
            } else if (!words.flags.drivenHere) {
                // Another page's job reads Stopped part way once its lease ends.
                consider(Date.parse(String(row.effectiveJob?.leaseExpiresAt)) - now + 50);
            }
        } else if (row?.state === 'queued') {
            const wait = runWaitsOutProviderFor(row, ctx.engine, now);
            if (wait) consider(waitTextChangesIn(wait.resumesInMs));
        } else if (row?.state === 'failed') {
            // A busy answer that was this browser's own closed page counts
            // down to when its claim lets go (quoteRunCoordinationOutcome).
            const hold = closedPageClaimHold(row.effectiveJob);
            if (hold && hold.freesAt > now) consider(Math.min(1000, hold.freesAt - now + 20));
        }
    }
    return soonest;
}

/**
 * When the words of a wait (waitText) next change: each second under a
 * minute, else when its rounded minutes do, so a long wait for a busy AI
 * service does not redraw the screen every second.
 */
function waitTextChangesIn(ms: number): number {
    const seconds = Math.round(Math.max(0, ms) / 1000);
    if (seconds <= 60) return 1000;
    const minutes = Math.round(seconds / 60);
    const nextWordsAt = minutes >= 2 ? (minutes * 60 - 30.5) * 1000 : 59_500;
    return Math.max(50, ms - nextWordsAt + 20);
}

// ─── The screen ─────────────────────────────────────────────────────────────

/**
 * A line of counts in the display states' own words ("2 waiting to start ·
 * 1 checking"), listing only the states that have a job, in display order.
 * `keys` limits it to some of them. Empty when there is none.
 */
export function quoteRunCountLine(
    counts: QuoteRunCounts,
    keys: ReadonlyArray<QuoteRunDisplay> = QUOTE_RUN_DISPLAY_ORDER.filter((key) => key !== 'ready'),
): string {
    return QUOTE_RUN_DISPLAY_ORDER
        .filter((key) => keys.includes(key) && counts[key] > 0)
        .map((key) => QUOTE_RUN_DISPLAY[key].count(counts[key]))
        .join(' · ');
}

/**
 * Why this tab has nothing to start while it runs nothing, in the words the
 * rows carry: a job another tab runs, a job being checked, jobs whose page
 * closed, or an empty run list. Null while this tab runs, or when a job is
 * waiting to start (Start the run then has a count).
 */
export function quoteRunIdleReason(rows: ReadonlyArray<SheetRow>, ctx: RowContext = {}): string | null {
    if (ctx.engine?.active) return null;
    if (runSelectionForRows(rows).jobCount > 0) return null;
    const all = rowsWords(rows, ctx);
    const otherTab = all.find((words) => words.display === 'processing' && !words.flags.drivenHere && words.reason);
    if (otherTab) return otherTab.reason;
    const checking = checkingWords(all.filter((words) => words.display === 'checking'));
    if (checking) return `${checking.headline} ${checking.detail}`;
    const stopped = all.filter((words) => words.display === 'stopped');
    if (stopped.length > 0) {
        const wait = stopped.reduce((most, words) => Math.max(most, reopenWaitSeconds(words)), 0);
        return `${jobsWord(stopped.length)} stopped part way. ${stoppedPartWayAdvice(stopped.length, wait, false)}`;
    }
    return 'Add at least one job to the run list first.';
}

/**
 * The headline and detail for jobs being checked: whether the page that ran
 * them is still open, known when their leases end. Null when there are none.
 */
function checkingWords(rows: ReadonlyArray<RowWords>): { headline: string; detail: string } | null {
    if (rows.length === 0) return null;
    const one = rows.length === 1;
    const who = one ? rows[0]!.name : jobsWord(rows.length);
    const them = one ? 'it' : 'them';
    const wait = rows.reduce((most, row) => Math.max(most, row.flags.leaseWaitSeconds), 0);
    return {
        headline: `Checking whether ${who} ${one ? 'is' : 'are'} still running.`,
        detail: `Another tab may still be running ${them}, or the page that ran ${them} has closed. QuoteMaker knows within ${wait} s.`,
    };
}

const SECTION_ORDER: Array<{ key: SectionKey; title: string }> = [
    { key: 'running', title: 'RUNNING NOW' },
    // A job whose page may have closed: not running, not stopped, until its
    // lease ends.
    { key: 'checking', title: 'CHECKING' },
    { key: 'needs-you', title: 'NEEDS YOU' },
    { key: 'drafts', title: 'DRAFT READY TO CHECK' },
    { key: 'waiting', title: 'WAITING TO START' },
    { key: 'not-run', title: 'NOT RUN' },
    { key: 'parked', title: 'PARKED' },
];

const UPDATE_WAITING = new Set(['reload-pending-authoring', 'reloading']);

function standardMore(drafts: number): ViewControl[] {
    const more: ViewControl[] = [{ kind: 'pick-folder', label: 'Pick files from a folder' }];
    if (drafts > 0) more.push({ kind: 'hide-drafts', label: `Hide drafts from this list (${drafts})` });
    more.push({ kind: 'run-details', label: 'Show run details' });
    return more;
}

function rowControl(row: RowWords): ViewControl | null {
    if (!row.action) return null;
    return {
        kind: row.action.kind,
        label: row.action.label,
        projectId: row.projectId,
        jobId: row.jobId,
        ...(row.action.stage ? { stage: row.action.stage } : {}),
        ...(row.action.disabled ? { disabled: true } : {}),
    };
}

export function quoteRunView(input: QuoteRunViewInput): QuoteRunView {
    const now = Number(input?.now) || 0;
    const engine: ViewEngine = input?.engine || {};
    const run: ViewRun = input?.run || {};
    const rows = Array.isArray(input?.rows) ? input.rows : [];
    const ranks = assignRunPositions(rows, engine);

    // Remaining work for the open-during-a-run reason and the jobs to go:
    // every job the run has not started plus the one in progress, from each
    // row's own words, so neither a job whose page closed part way (NOT RUN),
    // another tab's job, nor a job waiting for the next run is counted as
    // one this run will do.
    const remaining = quoteRunCounts(rows, { engine, run, now }).inRun;
    const words = rows.map((row) => quoteRunRowWords(row, {
        engine,
        run,
        now,
        rank: ranks.get(String(row.projectId)) ?? null,
        runRemaining: remaining,
        subStep: input?.subStep ?? null,
    }));

    const bySection = new Map<SectionKey, RowWords[]>(SECTION_ORDER.map(({ key }) => [key, []]));
    for (const row of words) {
        if (row.section) bySection.get(row.section)!.push(row);
    }
    // Drafts: Stage 4 reviews first. Waiting: run order, Files read after.
    bySection.get('drafts')!.sort((a, b) => Number(b.flags.stage4Review) - Number(a.flags.stage4Review));
    bySection.get('waiting')!.sort((a, b) => (
        (ranks.get(a.projectId) ?? Number.MAX_SAFE_INTEGER) - (ranks.get(b.projectId) ?? Number.MAX_SAFE_INTEGER)
    ));

    const sections: ViewSection[] = SECTION_ORDER
        .map(({ key, title }) => {
            const sectionRows = bySection.get(key)!;
            return { key, title, head: `${title} · ${sectionRows.length}`, count: sectionRows.length, rows: sectionRows };
        })
        .filter((section) => section.count > 0);

    const count = (key: SectionKey) => bySection.get(key)!.length;
    const counts = {
        running: count('running'),
        checking: count('checking'),
        'needs-you': count('needs-you'),
        drafts: count('drafts'),
        waiting: count('waiting'),
        'not-run': count('not-run'),
        parked: count('parked'),
        total: 0,
    } as QuoteRunView['counts'];
    counts.total = SECTION_ORDER.reduce((sum, { key }) => sum + counts[key], 0);

    const running = bySection.get('running')!;
    // The job this tab's run is on: the one the engine names, else one its
    // runner has claimed. Never another tab's job: with no job of its own in
    // progress (a wait for a busy AI service) the run has none.
    const liveRow = running.find((row) => engine.currentJobId != null && String(row.jobId) === String(engine.currentJobId))
        || running.find((row) => row.flags.drivenHere)
        || null;
    const liveName = liveRow?.name || 'the current job';
    const waiting = counts.waiting;
    const needsYou = counts['needs-you'];
    const drafts = counts.drafts;
    const parked = counts.parked;

    const view = (
        state: QuoteRunViewState,
        headline: string,
        detail: string,
        extra: Partial<QuoteRunView> = {},
    ): QuoteRunView => ({
        state,
        headline,
        detail,
        notes: [],
        primary: null,
        primaryDisabledReason: null,
        quiet: null,
        more: standardMore(drafts),
        sections,
        counts,
        folderLine: null,
        ...extra,
    });

    const parkedNote = parked > 0 ? [`${parked} more ${plural(parked, 'is', 'are')} parked and will not run.`] : [];
    const stopNow: ViewControl = { kind: 'stop-now', label: 'Stop now' };
    const leftSentence = (suffix: string) => (waiting > 0
        ? ` The ${waiting} ${plural(waiting, 'job left stays', 'jobs left stay')} here${suffix}.`
        : '');
    const readingNow = engine.phase
        ? engine.phase === 'pre-processing'
        : !!(liveRow && isReadingJob(rows.find((row) => row.projectId === liveRow.projectId)?.effectiveJob));

    // S1 Stopping.
    if (run.stopping && engine.active) {
        return view('S1', 'Stopping the run.', `Saving what ${liveName} finished. This takes a few seconds.`, { more: [] });
    }

    // S2 Update waiting while the run goes on.
    if (engine.active && UPDATE_WAITING.has(String(input?.updateState || ''))) {
        const carryOn = waiting > 0 ? ' Press Carry on after the page reloads.' : '';
        return view(
            'S2',
            'A new version of QuoteMaker is ready.',
            `The run finishes ${liveRow ? liveName : 'the job it is on'}, then stops so the page can update.${leftSentence('')}${carryOn} Nothing for you to do now.`,
            { more: [stopNow] },
        );
    }

    // S3 Pausing: Stop after this job is armed.
    if (engine.active && engine.stopAfterCurrent) {
        const first = readingNow
            ? 'It finishes reading its files first, then the run stops.'
            : 'It finishes its draft first, then the run stops.';
        return view(
            'S3',
            `Pausing after ${liveName}.`,
            `${first}${leftSentence(' for Carry on')} Nothing for you to do until then.`,
            { quiet: { kind: 'keep-going', label: 'Keep going' }, more: [stopNow] },
        );
    }

    // S4 Running.
    if (engine.active && !engine.paused) {
        const notes: string[] = [];
        if (needsYou > 0) {
            notes.push(`${jobsWord(needsYou)} ${plural(needsYou, 'needs', 'need')} you. The run carries on without ${plural(needsYou, 'it', 'them')}.`);
        }
        notes.push(...parkedNote);
        const controls = { quiet: { kind: 'stop-after-this-job', label: 'Stop after this job' }, more: [stopNow], notes };
        const liveSource = liveRow ? rows.find((row) => row.projectId === liveRow.projectId) : null;
        const liveJob = liveSource?.effectiveJob || {};
        const started = Date.parse(String(liveJob.startedAt || ''));
        const onJob = Number.isFinite(started) ? minutesText(now - started) : 'under 1 min';
        const stalled = liveSource ? noProgressFor(liveSource, now) : null;
        const waitingRow = rows
            .filter((row) => row.state === 'queued')
            .map((row) => ({ row, wait: runWaitsOutProviderFor(row, engine, now) }))
            .find((entry) => entry.wait);

        let headline: string;
        if (readingNow) {
            // The jobs this run reads, from each row's own words: not a job
            // waiting for the next run, one whose page closed, or another
            // tab's, as the jobs to go.
            const readingRows = words.filter((row, index) => (
                rowIsInRun(row, engine)
                && (rows[index]?.effectiveJob?.phase || 'prepare-quote') === 'pre-processing'
            ));
            const done = readingRows.filter((row) => row.flags.filesRead).length;
            headline = `Reading files: ${done} of ${readingRows.length} ${plural(readingRows.length, 'job', 'jobs')} read.`;
        } else {
            headline = `Writing drafts: ${drafts} ready, ${remaining} to go.`;
        }

        if (!liveRow && waitingRow?.wait) {
            const name = jobFolderName(waitingRow.row);
            const what = waitingRow.wait.connection
                ? 'QuoteMaker could not reach its service. It tries'
                : 'The AI service is busy. QuoteMaker tries';
            return view('S4', headline, `${what} ${name} again in ${waitText(waitingRow.wait.resumesInMs)}. Nothing is lost.`, controls);
        }
        if (liveRow && stalled != null) {
            return view('S4', headline, `${liveName} has made no progress for ${stallText(stalled)}. Big photo sets can take a while. If the page looks frozen, Stop now keeps the stages it finished.`, controls);
        }
        if (readingNow) {
            const files = liveSource && Array.isArray(liveSource.effectiveJob?.selectedFileIds)
                ? `${filesWord(rowFileCount(liveSource))}, `
                : '';
            const now1 = liveRow ? `Now: ${liveName}, ${files}${onJob}. ` : '';
            return view('S4', headline, `${now1}Drafts start when every job's files are read. Nothing for you to do. Keep this tab open.`, controls);
        }
        const stage = numericStage(liveJob.currentStage) || 1;
        const sub = input?.subStep ? ` · ${input.subStep}` : '';
        const now2 = liveRow ? `Now: ${liveName}, Stage ${stage} of 5${sub}, ${onJob} on this job. ` : '';
        return view('S4', headline, `${now2}Nothing for you to do. Keep this tab open.`, controls);
    }

    // S5 Paused by the Assistant.
    if (engine.active && engine.paused) {
        return view(
            'S5',
            'The run is paused between jobs.',
            'Nothing is running and nothing is being charged. Drafts open once the run carries on or stops.',
            { primary: { kind: 'resume', label: 'Carry on' }, more: [{ kind: 'stop-here', label: 'Stop here' }] },
        );
    }

    // S6 Another tab: a live job and no engine here. A job this tab's own run
    // claimed is never live once the engine is idle (processingFlags), so a
    // live row here is another tab's, even beside an old marker.
    const otherTab = !engine.active ? running.find((row) => row.flags.live) : null;
    if (otherTab) {
        const seconds = otherTab.flags.leaseWaitSeconds;
        return view(
            'S6',
            'This run is going in another tab or window.',
            `Its controls are there. If that tab has closed, QuoteMaker offers to carry on here in ${seconds} s.`,
            { more: [] },
        );
    }

    // A job whose page may still be open (a duplicated tab's original), or
    // may have closed (a reload), until its lease ends. Like S6, nothing
    // starts here meanwhile: it may be another tab's run.
    const checking = !engine.active ? checkingWords(bySection.get('checking')!) : null;
    if (checking) return view('checking', checking.headline, checking.detail, { more: [] });

    // S7 to S10 arrive in slice 4 (interrupted runs, updates, faults, stops).

    // S11 Ready to start.
    if (waiting > 0) {
        const selection = runSelectionForRows(rows);
        const n = selection.jobCount;
        const waitingRows = bySection.get('waiting')!;
        const first = (waitingRows.find((row) => row.flags.next) || waitingRows[0])?.name || '';
        const notes: string[] = [];
        if (needsYou > 0) {
            notes.push(`${jobsWord(needsYou)} ${plural(needsYou, 'needs', 'need')} you. ${plural(needsYou, 'It is', 'They are')} not in this run.`);
        }
        notes.push(...parkedNote);
        const label = `Start the run · ${jobsWord(n)}`;
        if (input?.adding) {
            return view(
                'S11',
                `${jobsWord(n)} ${plural(n, 'is', 'are')} ready to run.`,
                `Adding jobs to the list: ${input.adding.done} of ${input.adding.total}. Start the run when that finishes.`,
                {
                    notes,
                    primary: { kind: 'start-run', label, ids: selection.ids, disabled: true },
                    primaryDisabledReason: 'Still adding jobs.',
                },
            );
        }
        const sequence = n === 1
            ? `QuoteMaker reads the files for ${first} first, then writes its draft.`
            : `QuoteMaker reads the files for all ${n} first, then writes a draft for each, one at a time, starting with ${first}.`;
        return view(
            'S11',
            `${jobsWord(n)} ${plural(n, 'is', 'are')} ready to run.`,
            `${sequence} Each draft costs AI credit. Nothing is sent to anyone. Keep this tab open while it runs.`,
            { notes, primary: { kind: 'start-run', label, ids: selection.ids } },
        );
    }

    // S12 Needs you, nothing to run.
    if (needsYou > 0) {
        const failed = bySection.get('needs-you')!;
        const first = failed[0]!;
        const sourceJob = (row: RowWords) => rows.find((candidate) => candidate.projectId === row.projectId)?.effectiveJob;
        // The row's whole words, less any that name a control only the job
        // panel has: the headline's one button is the row's action.
        const headlineDetail = (row: RowWords) => {
            const job = sourceJob(row);
            const coordination = quoteRunCoordinationOutcome(job, now);
            return coordination ? coordinationDetail(job, coordination, 'headline') : row.detail;
        };
        if (failed.length === 1) {
            return view('S12', `${first.name} needs you.`, headlineDetail(first), { primary: rowControl(first), notes: parkedNote });
        }
        const retryable = (row: RowWords) => {
            const job = sourceJob(row);
            return row.action?.kind === 'retry'
                && !quoteRunCoordinationOutcome(job, now)
                && classifyFailedQueueRecovery(job) === 'continue-quote'
                && canRetryFailedQueueJob(job)
                && !row.flags.repeatedFailure;
        };
        const runWide = (row: RowWords) => isRunWideFailureCode(sourceJob(row)?.lastErrorCode);
        const retryAll: ViewControl = {
            kind: 'retry-all',
            label: `Try the ${failed.length} again`,
            ids: failed.map((row) => row.jobId).filter((id): id is number | string => id != null),
        };
        // A run-wide stop (credit, budget, sign-in, AI service not set up,
        // connection) is not fixed by a retry alone. When every job stopped
        // the same way, the headline says that one fix first, in the words
        // each row carries, and one press tries them all once it is done.
        if (failed.every(runWide) && failed.every(retryable) && failed.every((row) => row.detail === first.detail)) {
            return view('S12', `${failed.length} jobs need you.`, headlineDetail(first), { primary: retryAll, notes: parkedNote });
        }
        // "A retry can fix" only when every row's own words end in a plain
        // Try again. Words that ask for something first (name the job,
        // reconnect a folder, wait for another tab) would send the job
        // straight into the same stop, paying for its AI again. So would a
        // stop QuoteMaker has no plain words for (the generic family, or a
        // code no family knows, such as the engine's PIPELINE_STAGE_FAILED
        // for a throw with no code): its words end in the row's own Try
        // again, but nothing says a retry fixes it.
        const plainRetry = (row: RowWords) => /(?:^|[.!?]\s+)Try again\.$/.test(row.detail.trim());
        const hasPlainWords = (row: RowWords) => {
            const family = failureFamilyFor(sourceJob(row)?.lastErrorCode);
            return family !== null && family !== 'generic';
        };
        const safeRetry = failed.every((row) => retryable(row) && !runWide(row) && hasPlainWords(row) && plainRetry(row));
        if (safeRetry) {
            return view(
                'S12',
                `${failed.length} jobs need you.`,
                'Each stopped for a reason a retry can fix. Trying again keeps the stages already saved.',
                { primary: retryAll, notes: parkedNote },
            );
        }
        // A run-wide or coordination stop's fix is outside the job, so its
        // whole words lead, not just what happened.
        const whole = runWide(first) || quoteRunCoordinationOutcome(sourceJob(first), now);
        return view('S12', `${failed.length} jobs need you.`, `The first is ${first.name}: ${whole ? headlineDetail(first) : `${first.sentence}.`}`, {
            primary: rowControl(first),
            notes: parkedNote,
        });
    }

    // S13 Parked only.
    if (parked > 0) {
        const parkedRows = bySection.get('parked')!;
        const addable = parkedRows.filter((row) => row.action?.kind === 'add-to-run');
        const waitingOnly = parkedRows.every((row) => row.state === 'waiting_files');
        const firstParked = parkedRows[0]!;
        const review: ViewControl = { kind: 'review-row', label: `Review ${firstParked.name}`, projectId: firstParked.projectId };
        if (waitingOnly) {
            return view(
                'S13',
                `${jobsWord(parked)} ${plural(parked, 'is', 'are')} waiting on files.`,
                'When the last files arrive, open the job and press Files are all in. A job can also add itself after 30 quiet minutes.',
                { primary: review },
            );
        }
        return view(
            'S13',
            `${jobsWord(parked)} ${plural(parked, 'is', 'are')} set up but not in the run.`,
            'Add them, then start the run. Parked jobs keep their file picks.',
            {
                primary: addable.length > 0
                    ? { kind: 'add-all', label: `Add ${addable.length} to the run`, projectIds: addable.map((row) => row.projectId) }
                    : review,
            },
        );
    }

    // S14 All done.
    if (drafts > 0) {
        const draftRows = bySection.get('drafts')!;
        const first = draftRows[0]!;
        const reviews = draftRows.filter((row) => row.flags.stage4Review).length;
        const reviewNote = reviews > 0
            ? ` ${reviews} ${plural(reviews, 'needs', 'need')} a look at the scope in Stage 4 first.`
            : '';
        return view(
            'S14',
            drafts === 1 ? '1 draft is ready to check.' : `All ${drafts} drafts are ready to check.`,
            `Nothing has been sent to anyone.${reviewNote}`,
            {
                primary: {
                    kind: 'check-draft',
                    label: 'Check the first draft',
                    projectId: first.projectId,
                    ...(first.flags.stage4Review ? { stage: 4 } : {}),
                },
            },
        );
    }

    // Until slice 4: jobs whose page closed part way, and nothing else to do.
    const stopped = counts['not-run'];
    if (stopped > 0) {
        // The rows' own wait, the longest of them (quoteRunStoppedPartWay).
        const wait = bySection.get('not-run')!.reduce((most, row) => Math.max(most, reopenWaitSeconds(row)), 0);
        return view(
            'interim-stopped',
            `${jobsWord(stopped)} stopped part way.`,
            stoppedPartWayAdvice(stopped, wait, !!engine.active),
        );
    }

    // S15 Idle.
    return view(
        'S15',
        'No jobs yet.',
        'Pick a folder that holds your job folders. QuoteMaker lists each job here. Nothing runs and nothing is charged until you press Start the run.',
        {
            primary: { kind: 'pick-folder', label: 'Pick files from a folder' },
            more: [{ kind: 'folder-help', label: 'How to lay out job folders' }],
        },
    );
}

// ─── Browse's queue controls ────────────────────────────────────────────────

/**
 * What Browse's queue controls do for a folder: the box beside it in the list
 * and the queue button in its panel. Browse keeps no mapping of its own: it
 * reads the folder's row words, which the sheet, the job panel, the drawer and
 * the rail print.
 *
 *  - 'queue': Browse's own add. A folder with no job on the list, or a draft.
 *  - 'row': a parked job, or one waiting on files. The row's own step, as the
 *    sheet offers it: Add to the run, Pick files, Files are all in, Decide N
 *    files. A job waiting to start: the row's Leave out, which parks it with
 *    its picks (review of 97f9e0dd: Browse's own take-off called dequeue,
 *    which deletes a job that was added with Queue and never parked). And a
 *    stopped job (display 'failed'): its one next step, by its row's name
 *    (Try again, Restore folder access, Open the job, Review latest project,
 *    Read the files again, Start over, ...).
 *  - 'held': a job Browse can neither add nor take off, and why, in its row's
 *    words: it runs, here or in another tab, it is being checked, its page
 *    closed part way, or its files are read and it waits for its draft. Also
 *    a stopped job whose next step is outside Quote Run (a quote request
 *    Intake finishes), which offers no step anywhere.
 *    `onRun` ticks the box: the run has it, or may have it.
 */
export type QuoteRunBrowseStep =
    | { kind: 'queue' }
    | { kind: 'row'; action: RowAction }
    | { kind: 'held'; reason: string; onRun: boolean };

const lowerFirst = (text: string) => (text ? text.charAt(0).toLowerCase() + text.slice(1) : text);

export function quoteRunBrowseStep(words: RowWords | null | undefined): QuoteRunBrowseStep {
    if (!words) return { kind: 'queue' };
    switch (words.display) {
        case 'staged':
        case 'waiting_files':
            return words.action ? { kind: 'row', action: words.action } : { kind: 'queue' };
        case 'processing':
            // Another tab's job says where its controls are. This tab's own
            // job is stopped with the run bar's Stop the run.
            return {
                kind: 'held',
                onRun: true,
                reason: !words.flags.drivenHere && words.reason
                    ? words.reason
                    : 'Running now. Stop the run before starting this job again.',
            };
        case 'checking':
            return { kind: 'held', onRun: true, reason: words.reason || words.sentence };
        case 'stopped':
            return { kind: 'held', onRun: false, reason: words.reason || words.sentence };
        case 'failed':
            // A stopped job's next step is its row's, as the sheet, the job
            // panel, the drawer and Browse's panel offer it. Browse keeps no
            // recovery mapping of its own for it (review of 6da18a4e: its
            // list offered Retry for a job those four send to Intake).
            return words.action
                ? { kind: 'row', action: words.action }
                : { kind: 'held', onRun: false, reason: words.reason || words.sentence };
        case 'queued': {
            // Waiting to start: the row's own take-off, Leave out, as the
            // sheet, the job panel, the rail and the drawer run it.
            if (!words.flags.filesRead) return words.action ? { kind: 'row', action: words.action } : { kind: 'queue' };
            // It waits for its draft. Adding it again would read its files
            // again, and the sheet offers no Leave out for it.
            const unconfirmed = words.flags.proofUnknown && words.reason ? ` ${words.reason}` : '';
            return {
                kind: 'held',
                onRun: true,
                reason: `Already on the run list. ${words.chip}: ${lowerFirst(words.sentence)}.${unconfirmed}`,
            };
        }
        default:
            return { kind: 'queue' };
    }
}
